/**
 * Age Verification Page
 *
 * DOB input page for COPPA compliance. Collects date of birth,
 * calculates age, and either redirects to registration or collects
 * parent email for users under 13.
 */

import { useState } from 'react'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'

const months = Array.from({ length: 12 }, (_, i) => ({
  value: i + 1,
  label: new Date(2000, i).toLocaleString('default', { month: 'long' }),
}))

const days = Array.from({ length: 31 }, (_, i) => i + 1)

const currentYear = new Date().getFullYear()
const years = Array.from({ length: 15 }, (_, i) => currentYear - i)

export function AgeVerification() {
  const navigate = useNavigate()
  const [month, setMonth] = useState('')
  const [day, setDay] = useState('')
  const [year, setYear] = useState('')
  const [showParentForm, setShowParentForm] = useState(false)
  const [parentEmail, setParentEmail] = useState('')
  const [parentEmailSent, setParentEmailSent] = useState(false)
  const [error, setError] = useState('')

  const calculateAge = (birthDate: Date): number => {
    const today = new Date()
    let age = today.getFullYear() - birthDate.getFullYear()
    const monthDiff = today.getMonth() - birthDate.getMonth()
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--
    }
    return age
  }

  const handleSubmit = () => {
    setError('')

    if (!month || !day || !year) {
      setError('Please select your full date of birth.')
      return
    }

    const birthDate = new Date(Number(year), Number(month) - 1, Number(day))
    const age = calculateAge(birthDate)

    if (age < 13) {
      setShowParentForm(true)
    } else {
      navigate('/register', { state: { ageVerified: true } })
    }
  }

  const handleParentEmailSubmit = () => {
    if (!parentEmail) {
      setError('Please enter a parent or guardian email address.')
      return
    }
    setError('')
    setParentEmailSent(true)
  }

  return (
    <div className="min-h-screen bg-background-secondary">
      {/* Nav */}
      <nav className="px-8 py-4 bg-background border-b border-border flex items-center justify-between">
        <RouterLink to="/" className="hover:no-underline">
          <div className="flex items-center gap-1">
            <div className="w-7 h-7 rounded-full bg-accent flex items-center justify-center">
              <span className="text-sm font-bold text-foreground">D</span>
            </div>
            <span className="text-lg font-bold text-foreground">ocurious</span>
          </div>
        </RouterLink>
        <RouterLink to="/login" className="text-sm text-primary hover:underline">
          Sign In
        </RouterLink>
      </nav>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-8 py-12">
        <div className="bg-background p-8 rounded-[var(--radius-card)] border border-border">
          <div className="flex flex-col gap-6">
            <h1 className="text-2xl font-bold text-foreground">Age Verification</h1>
            <p className="text-sm text-foreground-muted">
              To comply with the Children's Online Privacy Protection Act (COPPA), we need to verify your age
              before you can create an account.
            </p>

            {!showParentForm && !parentEmailSent && (
              <div className="flex flex-col gap-4">
                <h2 className="text-lg font-semibold text-foreground">Enter Your Date of Birth</h2>

                <div className="flex gap-4">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-foreground mb-1">Month</p>
                    <select
                      value={month}
                      onChange={(e) => setMonth(e.target.value)}
                      className="w-full px-3 py-2 rounded-[var(--radius-button)] border border-border bg-transparent text-sm text-foreground"
                    >
                      <option value="">Month</option>
                      {months.map((m) => (
                        <option key={m.value} value={m.value}>{m.label}</option>
                      ))}
                    </select>
                  </div>

                  <div className="flex-1">
                    <p className="text-sm font-medium text-foreground mb-1">Day</p>
                    <select
                      value={day}
                      onChange={(e) => setDay(e.target.value)}
                      className="w-full px-3 py-2 rounded-[var(--radius-button)] border border-border bg-transparent text-sm text-foreground"
                    >
                      <option value="">Day</option>
                      {days.map((d) => (
                        <option key={d} value={d}>{d}</option>
                      ))}
                    </select>
                  </div>

                  <div className="flex-1">
                    <p className="text-sm font-medium text-foreground mb-1">Year</p>
                    <select
                      value={year}
                      onChange={(e) => setYear(e.target.value)}
                      className="w-full px-3 py-2 rounded-[var(--radius-button)] border border-border bg-transparent text-sm text-foreground"
                    >
                      <option value="">Year</option>
                      {years.map((y) => (
                        <option key={y} value={y}>{y}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {error && (
                  <p className="text-sm text-red-500">{error}</p>
                )}

                <Button onClick={handleSubmit}>
                  Continue
                </Button>
              </div>
            )}

            {showParentForm && !parentEmailSent && (
              <div className="flex flex-col gap-4">
                <h2 className="text-lg font-semibold text-foreground">Parental Consent Required</h2>
                <p className="text-sm text-foreground-muted">
                  Because you are under 13, we need a parent or guardian's permission before you can create
                  an account. Please enter your parent or guardian's email address below, and we will send
                  them a consent request.
                </p>

                <div>
                  <p className="text-sm font-medium text-foreground mb-1">Parent/Guardian Email</p>
                  <Input
                    type="email"
                    placeholder="parent@example.com"
                    value={parentEmail}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setParentEmail(e.target.value)}
                  />
                </div>

                {error && (
                  <p className="text-sm text-red-500">{error}</p>
                )}

                <Button onClick={handleParentEmailSubmit}>
                  Send Consent Request
                </Button>
              </div>
            )}

            {parentEmailSent && (
              <div className="flex flex-col gap-4">
                <h2 className="text-lg font-semibold text-foreground">Consent Request Sent</h2>
                <p className="text-sm text-foreground-muted">
                  We've sent a consent request to <strong>{parentEmail}</strong>. Once your parent or guardian
                  approves, you'll be able to create your account. Please ask them to check their email.
                </p>
                <Button asChild variant="outline">
                  <RouterLink to="/">Return Home</RouterLink>
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
